<template>
  <div class="dtf-progress-bar">
    <div
      v-if="!showErrorState"
      :class="{
        'dtf-progress-bar__line-wrap': !isStandardArtwork,
        'dtf-progress-bar__line-wrap-ab-comparison': isStandardArtwork
      }"
    >
      <div
        v-if="!isStandardArtwork"
        class="dtf-progress-bar__line"
        :style="{ width: currentProgressPercentage }"
      ></div>
      <div v-if="isStandardArtwork" class="dtf-progress-bar__line-ab-comparison"></div>
    </div>
    <div
      :class="{
        'dtf-progress-bar__wrapper': !isStandardArtwork,
        'dtf-progress-bar__wrapper-ab-comparison': isStandardArtwork
      }"
    >
      <button
        v-if="showErrorState && showModal"
        type="button"
        class="dtf-progress-bar__remove-button"
        @click="deleteArtwork"
      >
        <UseSvg icon="trash" class="dtf-progress-bar__trash-icon" />
        Remove
      </button>
      <template v-if="!showErrorState">
        <div class="dtf-progress-bar__value">
          {{ progressText }}
          <span class="dtf-progress-bar__remaining-time">
            (Less than {{ t("minute", remainingTime) }} left)
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useI18n } from "vue-i18n";
import UseSvg from "common/vue/components/UseSvg.vue";
import { DTF_PROGRESS_MAPPING } from "common/utils/progress-bar/constants";
import { useDtfStore } from "./store";

const UPDATE_INTERVAL = 50.0;

export default {
  name: "DtfProgressBar",
  components: { UseSvg },
  props: {
    artwork: {
      type: Object,
      default: null
    },
    index: {
      type: Number,
      default: null
    }
  },
  emits: ["onClickRemove"],
  setup() {
    return { ...useI18n() };
  },
  data() {
    return {
      previousTimeStamp: 0,
      updateInterval: UPDATE_INTERVAL,
      isRunning: true,
      currentProgress: this.artwork.currentProgress || 0,
      currentProgressPercentage: "",
      progressText: "",
      timeoutHandle: null
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "isRetrying",
      "showModal",
      "actionCableConnected",
      "isSpangle",
      "isStandardArtwork",
      "artworks"
    ]),
    isOnline() {
      return (
        this.progressState === "online" && !this.artwork.retryFailed && this.actionCableConnected
      );
    },
    progressState() {
      if (this.progressError) return "error";
      if (navigator.onLine && this.actionCableConnected) return "online";

      return "offline";
    },
    showErrorState() {
      return this.progressError || this.artwork.retryFailed;
    },
    showProcessing() {
      return this.isOnline;
    },
    progressError() {
      return this.artwork.uploadError;
    },
    progress() {
      return this.artwork.progress;
    },
    progressMapIndex() {
      return this.isSpangle ? 1 : 0;
    },
    fileSize() {
      return Math.floor((this.artwork?.file?.size || 0) * 0.000001 || 0);
    },
    remainingTime() {
      const spangleTime = this.isSpangle ? 1 : 0;

      if (Math.ceil(this.fileSize / 10) < 1) {
        return 1 + spangleTime;
      }
      return Math.min(Math.ceil(this.fileSize / 10), 15) + spangleTime;
    },
    artworkPresentInStore() {
      return this.artworks.findIndex(artwork => artwork.id === this.artwork.id) > -1;
    }
  },
  mounted() {
    // retain the progress from previous value if it exists
    this.currentProgress = this.artwork.currentProgress || 0;

    window.addEventListener("online", this.handleOnlineStatusChange);
    window.addEventListener("offline", this.handleOnlineStatusChange);
    this.incrementProgress();
  },
  beforeUnmount() {
    window.removeEventListener("online", this.handleOnlineStatusChange);
    window.removeEventListener("offline", this.handleOnlineStatusChange);

    // Every time artwork object is updated the component is re-rendered, causing the progress to
    // reset and the setTimeout persisted from the previous render. This fix ensures that the
    // progress is saved before the component is being re-rendered.
    this.cleanupAndSaveState();
  },
  methods: {
    ...mapActions(useDtfStore, [
      "removeArtworkByIndex",
      "setIsRetrying",
      "updateArtworkValues",
      "updateArtworkValuesById"
    ]),
    deleteArtwork() {
      if (this.index === null) {
        this.$emit("onClickRemove");
      } else {
        this.removeArtworkByIndex(this.index);
      }
    },
    handleOnlineStatusChange() {
      this.online = navigator.onLine;

      // Reset percentage when going offline
      if (!this.isOnline) {
        this.isRunning = false;
        return;
      }

      if (this.online) {
        this.isRunning = true;
        this.setIsRetrying(false);
      }
    },
    incrementProgress() {
      if (this.currentProgress >= this.progress && this.artworkPresentInStore) {
        this.updateArtworkValues({
          index: this.artwork.originalIndex,
          currentProgress: this.currentProgress
        });
        return;
      }

      const keys = Object.keys(DTF_PROGRESS_MAPPING);
      const currentStageKey = keys.find(
        key => DTF_PROGRESS_MAPPING[key].percentage[this.progressMapIndex] > this.currentProgress
      );

      const currentStage = DTF_PROGRESS_MAPPING[currentStageKey];
      let counterInterval =
        currentStage.interval[this.progressMapIndex] || currentStage.interval[0];
      const { rate } = currentStage;

      const nextStageKey = keys.find(
        key => DTF_PROGRESS_MAPPING[key].percentage[this.progressMapIndex] === this.progress
      );
      const nextStage = DTF_PROGRESS_MAPPING[nextStageKey];

      this.currentProgress = parseFloat((this.currentProgress + rate).toFixed(2));

      const speedUp = keys.indexOf(nextStageKey) > keys.indexOf(currentStageKey);
      if (speedUp) {
        counterInterval = currentStage.catchUpInterval;

        if (nextStage.skipCatchUp) {
          this.currentProgress = nextStage.percentage[this.progressMapIndex];
        }
      }

      this.setProgressPercentage();
      this.setProgressText();

      this.timeoutHandle = setTimeout(this.incrementProgress, counterInterval);
    },
    setProgressPercentage() {
      this.currentProgressPercentage = this.progressError
        ? "100%"
        : `${this.currentProgress.toFixed(2)}%`;
    },
    setProgressText() {
      this.progressText = this.currentProgressPercentage;
    },
    cleanupAndSaveState() {
      if (this.timeoutHandle) {
        clearTimeout(this.timeoutHandle);
      }

      if (!this.artworkPresentInStore) {
        this.updateArtworkValues({ index: this.index, currentProgress: this.currentProgress });
      }
    }
  },
  watch: {
    progress() {
      this.incrementProgress();
    },
    progressState() {
      this.setProgressText();
    },
    isOnline() {
      this.setProgressText();
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/mixin";

.dtf-progress-bar {
  width: 100%;

  &__wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
  }

  &__wrapper-ab-comparison {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 1;
  }

  &__value {
    font-size: 12px;
    font-weight: 700;
  }

  &__remaining-time {
    font-weight: normal;
  }

  &__file {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__error-message {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $red-light;
  }

  &__line-wrap {
    display: inline-block;
    position: relative;
    width: 100%;
    height: 8px;
    margin: 8px 0;
    border-radius: 24px;
    background: $white;
    padding: 1px 0;

    @include with-tablet {
      padding: 0;
    }
  }

  &__line {
    position: absolute;
    left: 0;
    max-width: 100%;
    height: 100%;
    border-radius: 24px;
    transition: width 200ms linear;
    background-color: #46ab81;
  }

  &__line-wrap-ab-comparison {
    display: inline-block;
    position: relative;
    width: 100%;
    height: 4px;
    margin: 6px 0;
    border-radius: 24px;
    background: $gray-border;
  }

  &__line-ab-comparison {
    position: relative;
    height: 2px;
    border-radius: 16px;
    transition: clip-path 200ms linear;
    background: linear-gradient(90deg, #8e56f3 0%, #ea78d0 53.5%, #ff7237 97.5%);
    clip-path: polygon(
      v-bind("currentProgressPercentage") 0,
      v-bind("currentProgressPercentage") 100%,
      0% 100%,
      0% 0%
    );

    @include with-tablet {
      height: 4px;
    }
  }

  &__remove-button {
    font-size: 13px;
    font-weight: 700;
    line-height: 20px;
    color: $primary-light;
    background: none;
    border: none;
    display: flex;
    align-items: center;
    height: 32px;
    right: 6px;
    top: -12px;
    margin-left: auto;

    @include with-large-desktop {
      top: -8px;
    }
  }

  &__trash-icon {
    width: 18px;
    height: 18px;
    margin-right: 3px;
  }
}
</style>
